import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PencilSimple, Trash, CurrencyDollar, Warning, MagnifyingGlass, X } from "@phosphor-icons/react";
import { Header, AddBtn, Modal, In, Sel, SaveBtn, IconBtn, DivisionTabs } from "./shared";
import PlayerDetailsModal from "./PlayerDetailsModal";

const norm = (s) => (s || "").toLowerCase().replace(/\s+/g, " ").trim();
const eligOk = (p) => Boolean(p.eligible && p.profile_complete);
const eligReasons = (p) => [...(p.eligibility_reasons || []), ...(p.profile_warnings || [])];
const waiverDone = (p) => p.waiver_status === "signed" || p.waiver_signed === true;

export default function PlayersTab() {
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const empty = { name: "", team_id: "", number: "", position: "", photo_url: "", date_of_birth: "", email: "", phone: "", games_played: 0, points: 0, rebounds: 0, assists: 0, steals: 0, blocks: 0, threes: 0 };
  const [form, setForm] = useState(empty);
  const [division, setDivision] = useState("Open");
  const [compliancePlayer, setCompliancePlayer] = useState(null);
  const [search, setSearch] = useState("");
  const [eligFilter, setEligFilter] = useState("all"); // all | eligible | not
  const [waiverFilter, setWaiverFilter] = useState("all"); // all | completed | not
  const [teamFilter, setTeamFilter] = useState("");

  const load = useCallback(() => { api.get("/players").then((r) => setPlayers(r.data)); api.get("/teams").then((r) => setTeams(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const teamName = (id) => teams.find((t) => t.id === id)?.name || "Free Agent";
  const divisionOf = (id) => teams.find((t) => t.id === id)?.division || "Open";
  // Reset the team filter whenever the division changes (teams differ per division).
  useEffect(() => { setTeamFilter(""); }, [division]);
  const divisionTeams = teams.filter((t) => (t.division || "Open") === division);

  const q = norm(search);
  const visiblePlayers = players.filter((p) => {
    if (divisionOf(p.team_id) !== division) return false;
    if (teamFilter && p.team_id !== teamFilter) return false;
    if (eligFilter === "eligible" && !eligOk(p)) return false;
    if (eligFilter === "not" && eligOk(p)) return false;
    if (waiverFilter === "completed" && !waiverDone(p)) return false;
    if (waiverFilter === "not" && waiverDone(p)) return false;
    if (q && !norm(p.name).includes(q)) return false;
    return true;
  });
  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p }); setOpen(true); };

  const num = (v) => (v === "" ? 0 : parseInt(v, 10) || 0);
  const jerseyNum = (v) => { const s = String(v ?? "").trim(); return s === "" ? null : s; };
  const save = async (e) => {
    e.preventDefault(); setLoading(true);
    const payload = { ...form, number: jerseyNum(form.number), games_played: num(form.games_played), points: num(form.points), rebounds: num(form.rebounds), assists: num(form.assists), steals: num(form.steals), blocks: num(form.blocks), threes: num(form.threes) };
    try {
      if (editing) await api.put(`/players/${editing.id}`, payload);
      else await api.post("/players", payload);
      toast.success("Player saved"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const del = async (id) => { if (!window.confirm("Delete this player?")) return; await api.delete(`/players/${id}`); toast.success("Deleted"); load(); };

  const statFields = ["games_played", "points", "threes", "rebounds", "assists", "steals", "blocks"];

  return (
    <div data-testid="players-tab">
      <Header title="Players" action={<AddBtn onClick={openNew} label="Add Player" testid="add-player-btn" />} />
      <DivisionTabs value={division} onChange={setDivision} prefix="players" />

      {/* Search + filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4" data-testid="players-filter-bar">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by first, last or full name…"
            data-testid="players-search-input"
            className="w-full bg-[#1A1A1A] border border-white/15 pl-10 pr-9 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all"
          />
          {search && (
            <button onClick={() => setSearch("")} data-testid="players-search-clear" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"><X size={16} /></button>
          )}
        </div>
        <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} data-testid="players-team-filter" className="bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all sm:w-48">
          <option value="">All Teams</option>
          {divisionTeams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select value={eligFilter} onChange={(e) => setEligFilter(e.target.value)} data-testid="players-eligibility-filter" className="bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all sm:w-44">
          <option value="all">All Eligibility</option>
          <option value="eligible">Eligible</option>
          <option value="not">Not Eligible</option>
        </select>
        <select value={waiverFilter} onChange={(e) => setWaiverFilter(e.target.value)} data-testid="players-waiver-filter" className="bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all sm:w-48">
          <option value="all">All Waivers</option>
          <option value="completed">Waiver: Completed</option>
          <option value="not">Waiver: Not Completed</option>
        </select>
      </div>

      <div className="border border-white/10 bg-[#121212] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-white/50 uppercase text-xs tracking-wider">
              <th className="text-left py-3 px-4">#</th>
              <th className="text-left py-3 px-4">Player</th>
              <th className="text-left py-3 px-4">Team</th>
              <th className="text-center py-3 px-3">GP</th>
              <th className="text-center py-3 px-3">PTS</th>
              <th className="text-center py-3 px-3">REB</th>
              <th className="text-center py-3 px-3">AST</th>
              <th className="text-center py-3 px-3">Waiver</th>
              <th className="text-center py-3 px-3">Eligibility</th>
              <th className="text-right py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {visiblePlayers.map((p) => (
              <tr key={p.id} className="border-b border-white/5 last:border-0 hover:bg-white/5" data-testid="player-row">
                <td className="py-3 px-4 tabular-nums text-[#FF5722]">{p.number != null ? p.number : "-"}</td>
                <td className="py-3 px-4 font-semibold uppercase">
                  <button onClick={() => setCompliancePlayer(p)} data-testid="player-name-btn" title="Open player profile" className="inline-flex items-center gap-1.5 text-left hover:text-[#FF5722] transition-colors cursor-pointer">{p.name}
                    {p.profile_complete === false && <span title={`Profile incomplete: ${(p.profile_warnings || []).join(", ")}`} data-testid="profile-incomplete-flag" className="text-[#FFB300]"><Warning size={15} weight="fill" /></span>}
                  </button>
                </td>
                <td className="py-3 px-4 text-white/50">{teamName(p.team_id)}</td>
                <td className="text-center py-3 px-3 tabular-nums">{p.games_played}</td>
                <td className="text-center py-3 px-3 tabular-nums">{p.points}</td>
                <td className="text-center py-3 px-3 tabular-nums">{p.rebounds}</td>
                <td className="text-center py-3 px-3 tabular-nums">{p.assists}</td>
                <td className="text-center py-3 px-3">
                  {waiverDone(p)
                    ? <span className="text-[10px] uppercase font-bold bg-[#00E676]/20 text-[#00E676] px-2 py-0.5" data-testid="waiver-status-badge">Completed</span>
                    : <span className="text-[10px] uppercase font-bold bg-[#FF9500]/20 text-[#FF9500] px-2 py-0.5" data-testid="waiver-status-badge" title="Waiver not signed yet">Not Completed</span>}
                </td>
                <td className="text-center py-3 px-3">
                  {eligOk(p)
                    ? <span className="text-[10px] uppercase font-bold bg-[#00E676]/20 text-[#00E676] px-2 py-0.5">Eligible</span>
                    : <span className="text-[10px] uppercase font-bold bg-[#FF3B30]/20 text-[#FF3B30] px-2 py-0.5" title={eligReasons(p).join(" · ")}>Not Eligible</span>}
                </td>
                <td className="py-3 px-4 text-right whitespace-nowrap">
                  <IconBtn onClick={() => setCompliancePlayer(p)} testid="manage-compliance-btn"><CurrencyDollar size={18} /></IconBtn>
                  <IconBtn onClick={() => openEdit(p)} testid="edit-player-btn"><PencilSimple size={18} /></IconBtn>
                  <IconBtn onClick={() => del(p.id)} danger testid="delete-player-btn"><Trash size={18} /></IconBtn>
                </td>
              </tr>
            ))}
            {visiblePlayers.length === 0 && <tr><td colSpan={10} className="text-center py-10 text-white/40" data-testid="players-empty">{search || teamFilter || eligFilter !== "all" || waiverFilter !== "all" ? "No players match your search or filters." : `No players in the ${division} division yet.`}</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Player" : "Add Player"}>
        <form onSubmit={save} className="space-y-4">
          <In label="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="player-name-input" />
          <div className="grid grid-cols-2 gap-3">
            <In label="Date of Birth" type="date" value={form.date_of_birth ? String(form.date_of_birth).slice(0, 10) : ""} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="player-dob-input" />
            <In label="Email" type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <In label="Phone (optional)" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Sel label="Team" value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })} data-testid="player-team-select">
              <option value="">Free Agent</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Sel>
            <In label="Jersey # (00–9000, blank if none)" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={4} value={form.number ?? ""} onChange={(e) => setForm({ ...form, number: e.target.value.replace(/[^0-9]/g, "") })} data-testid="player-jersey-input" />
          </div>
          <In label="Position" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
          <div className="grid grid-cols-3 gap-3">
            {statFields.map((f) => (
              <In key={f} label={f === "threes" ? "3PM" : f.replace("_", " ")} type="number" value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })} />
            ))}
          </div>
          <SaveBtn loading={loading} testid="save-player-btn" />
          <p className="text-[11px] text-white/40 text-center pt-1">Add or take a profile photo from the <span className="text-white/70 font-semibold">$ Manage</span> screen after saving.</p>
        </form>
      </Modal>

      {compliancePlayer && (
        <PlayerDetailsModal
          player={compliancePlayer}
          onClose={() => setCompliancePlayer(null)}
          onSaved={(updated) => { setCompliancePlayer(updated); setPlayers((prev) => prev.map((x) => (x.id === updated.id ? updated : x))); }}
        />
      )}
    </div>
  );
}
